import React from 'react';
import { ImageIcon } from 'lucide-react';
import { Container, Section } from '../components/ui/Section';
import { PageHeader } from '../components/ui/PageHeader';
import { LeaderCard } from '../components/about/LeaderCard';
import { ButtonExternal, ButtonLink } from '../components/ui/Button';
import { Reveal, stagger } from '../components/motion/Reveal';
import { DoodleScatter } from '../components/brand/DoodleScatter';
import { leadership } from '../data/leadership';
import { emailAddress, externalLinks } from '../data/site';

/**
 * Dedicated leadership page. The cards themselves are unchanged — they simply
 * become the focus of their own page instead of one section on /about.
 */
export function MeetTheTeam() {
  return <>
      <PageHeader eyebrow="Meet the Team" title="The BCM leadership team" intro="These are the students and leaders helping serve the BCM community this year." seed="meet-the-team-header" data-id="element-560" data-mp="4dka7tzpw1gl" />

      <Section className="relative overflow-hidden bg-[#fffdf6] py-14 sm:py-18" labelledBy="team-grid-heading" data-id="element-561" data-mp="267a0l66nj1ug">
        <DoodleScatter tone="white" seed="meet-the-team-grid" data-id="element-562" data-mp="2vv63tex1i96k" />
        <Container className="relative" data-id="element-563" data-mp="2vtld60dujadz">
          <h2 id="team-grid-heading" className="sr-only" data-id="element-564" data-mp="2qxeduqfygx50">
            BCM leaders
          </h2>
          {/*
            INTERNAL CONTENT NOTE (not rendered): a headshot for Brad has not been
            supplied yet. Add the hosted URL to data/leadership.ts.
           */}
          {/* Nine cards: the Director spans the mobile row so no card is orphaned. */}
          <ul className="grid grid-cols-2 gap-4 md:grid-cols-3" data-id="element-565" data-mp="39muf5fzgstmh">
            {leadership.map((leader, index) => <Reveal key={leader.id} as="li" delay={stagger(index % 3)} className={`h-full ${index === 0 ? 'col-span-2 md:col-span-1' : ''}`} data-id="element-566" data-mp="34o9g3jvy8fzm" data-mpi={index}>
                <LeaderCard leader={leader} index={index} data-id="element-567" data-mp="1t7jiuq9i6yi5" />
              </Reveal>)}
          </ul>
        </Container>
      </Section>

      {/* Worship Team */}
      <Section className="relative overflow-hidden bg-brand-blue py-14 sm:py-18" labelledBy="team-worship-heading" data-id="element-568" data-mp="pb8zwgbglhx9">
        <DoodleScatter tone="blue" seed="about-worship-team" data-id="element-569" data-mp="bag1hki1e2ov" />
        <Container className="relative grid items-center gap-10 lg:grid-cols-[1fr_0.85fr] lg:gap-14" data-id="element-570" data-mp="g9j5r2q704vs">
          <Reveal data-id="element-571" data-mp="1kwhucmi24sy">
            <h2 id="team-worship-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-572" data-mp="1kczwvwungex">
              Worship Team
            </h2>
            <p className="mt-5 max-w-xl font-sans text-[17px] leading-relaxed text-ink" data-id="element-573" data-mp="3orfabw248zex">
              BCM’s worship team helps lead Thursday nights through music and creates space for
              students to worship together. If you sing, play an instrument, or are interested in
              serving with the team, we’d love to hear from you.
            </p>
            <p className="mt-4 font-sans text-[17px] leading-relaxed text-ink" data-id="element-574" data-mp="3047omsstutng">
              If you want to join the worship team, fill out this form.
            </p>
            <ButtonExternal href={externalLinks.worshipTeamForm} className="mt-7" data-id="element-575" data-mp="3a35jedgujjew">
              Worship Team Interest Form
            </ButtonExternal>
          </Reveal>

          {/*
            Placeholder only. A verified worship-team or worship-night photo
            replaces this frame once supplied — never a generated team.
           */}
          <Reveal x={16} delay={0.12} data-id="element-576" data-mp="3f1gt8o755tx4">
            <div className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 rounded-[20px] border-2 border-brand-navy bg-brand-tan p-8 text-center" data-id="element-577" data-mp="3f0fh0gee31dt">
              <ImageIcon aria-hidden="true" className="h-7 w-7 text-brand-navy" data-id="element-578" data-mp="3nv3lw5g1yh23" />
              <p className="font-meta text-[15px] font-semibold text-brand-navy" data-id="element-579" data-mp="6nhwvlg9c6j3">Add Image here</p>
            </div>
          </Reveal>
        </Container>
      </Section>

      <Section className="bcm-paper relative overflow-hidden py-14 sm:py-18" labelledBy="team-cta-heading" data-id="element-580" data-mp="1v6chuux9tod9">
        <DoodleScatter tone="paper" seed="meet-the-team-cta" layout="narrow" data-id="element-581" data-mp="203uqslbag3l9" />
        <Container className="relative max-w-3xl text-center" data-id="element-582" data-mp="24vm6g0swoowm">
          <Reveal data-id="element-583" data-mp="24y8fy5zcxehh">
            <h2 id="team-cta-heading" className="font-serif text-3xl font-semibold leading-tight text-brand-navy sm:text-4xl" data-id="element-584" data-mp="29upd1pojb36p">
              Want to talk to someone on the team?
            </h2>
            <p className="mx-auto mt-4 max-w-xl font-sans text-[16px] leading-relaxed text-ink" data-id="element-585" data-mp="1h7303qtf2m76">
              Reach out any time — someone is always glad to answer a question.
            </p>
            <div className="mt-7 flex flex-col justify-center gap-3 sm:flex-row" data-id="element-586" data-mp="1r5ity1jtw9kd">
              <ButtonExternal href={externalLinks.getConnectedForm} data-id="element-587" data-mp="2xlco2jtqy8xh">Get Connected</ButtonExternal>
              <ButtonLink to="/about" variant="secondary" data-id="element-588" data-mp="32iuzcgv8l08n">
                About UCF BCM
              </ButtonLink>
            </div>
            <p className="mt-6 font-meta text-[14px] text-brand-navy" data-id="element-589" data-mp="qvrhh7alm9hw">
              Or email{' '}
              <a href={`mailto:${emailAddress}`} className="underline hover:no-underline" data-id="element-590" data-mp="lxpx8j1fs44u">
                {emailAddress}
              </a>
            </p>
          </Reveal>
        </Container>
      </Section>
    </>;
}